import { useState } from 'react'
import { useProject } from '../stores/ProjectContext'
import { useI18n } from '../stores/I18nContext'
import { useIsMobile } from '../hooks/useIsMobile'
import './DependencyView.css'

export default function DependencyView() {
  const { project, addDependency, removeDependency, projectRole } = useProject()
  const { language } = useI18n()
  const isMobile = useIsMobile()
  const isNorwegian = language === 'nb'
  // Dependency creation/deletion is gated to non-mobile to keep the phone UX
  // focused on viewing and reading. Editing happens on desktop.
  const canEdit = projectRole !== 'VIEWER' && !isMobile

  const [fromId, setFromId] = useState('')
  const [toId, setToId] = useState('')

  // Build a flat list of all checkpoints across all flows
  const allCheckpoints = []
  for (const flow of project.flows || []) {
    for (const cp of flow.checkpoints || []) {
      allCheckpoints.push({ flowId: flow.id, flowName: flow.name, flowColor: flow.color, cpId: cp.id, cpName: cp.name })
    }
  }

  // Build lookup map for rendering existing dependencies
  const checkpointMap = {}
  for (const item of allCheckpoints) {
    checkpointMap[item.cpId] = item
  }

  const handleAdd = () => {
    if (!fromId || !toId || fromId === toId) return
    addDependency(fromId, toId)
    setFromId('')
    setToId('')
  }

  return (
    <div className="dependency-view">
      <div className="dependency-view-content">
        <div className="dependency-view-header">
          <div>
            <h1>{isNorwegian ? 'Avhengigheter' : 'Dependencies'}</h1>
            <p className="dependency-view-subtitle">
              {isNorwegian
                ? 'Avhengigheter definerer hvilke sjekkpunkter som må fullføres før andre kan starte. De brukes til å identifisere den kritiske stien.'
                : 'Dependencies define which checkpoints must finish before others can start. They\'re used to identify the critical path.'}
            </p>
          </div>
        </div>

        {/* Existing dependencies */}
        <div className="dependency-section">
          <h2 className="dependency-section-title">
            {isNorwegian ? 'Eksisterende avhengigheter' : 'Existing dependencies'}
          </h2>

          {(project.dependencies || []).length === 0 ? (
            <p className="dependency-empty">
              {isNorwegian
                ? 'Ingen avhengigheter definert ennå.'
                : 'No dependencies defined yet.'}
            </p>
          ) : (
            <ul className="dependency-list">
              {(project.dependencies || []).map((dep, i) => {
                const from = checkpointMap[dep.from]
                const to = checkpointMap[dep.to]
                return (
                  <li key={i} className="dependency-item">
                    <div className="dependency-endpoints">
                      <span className="dependency-checkpoint">
                        {from ? (
                          <>
                            <span
                              className="dep-flow-dot"
                              style={{ backgroundColor: from.flowColor }}
                            />
                            <span className="dep-flow-name">[{from.flowName}]</span>
                            <span className="dep-cp-name">{from.cpName}</span>
                          </>
                        ) : (
                          <span className="dep-unknown">{dep.from}</span>
                        )}
                      </span>
                      <span className="dependency-arrow">→</span>
                      <span className="dependency-checkpoint">
                        {to ? (
                          <>
                            <span
                              className="dep-flow-dot"
                              style={{ backgroundColor: to.flowColor }}
                            />
                            <span className="dep-flow-name">[{to.flowName}]</span>
                            <span className="dep-cp-name">{to.cpName}</span>
                          </>
                        ) : (
                          <span className="dep-unknown">{dep.to}</span>
                        )}
                      </span>
                    </div>
                    {canEdit && (
                      <button
                        className="dep-delete-btn"
                        onClick={() => removeDependency(dep.from, dep.to)}
                        title={isNorwegian ? 'Slett avhengighet' : 'Delete dependency'}
                      >
                        ×
                      </button>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* Add dependency */}
        {canEdit && allCheckpoints.length >= 2 && (
          <div className="dependency-section">
            <h2 className="dependency-section-title">
              {isNorwegian ? 'Legg til avhengighet' : 'Add dependency'}
            </h2>
            <div className="dependency-add-form">
              <div className="dependency-add-selects">
                <div className="dependency-select-group">
                  <label>{isNorwegian ? 'Fra sjekkpunkt' : 'From checkpoint'}</label>
                  <select
                    value={fromId}
                    onChange={e => setFromId(e.target.value)}
                    className="dependency-select"
                  >
                    <option value="">{isNorwegian ? '— velg —' : '— select —'}</option>
                    {allCheckpoints.map(item => (
                      <option key={item.cpId} value={item.cpId}>
                        [{item.flowName}] {item.cpName}
                      </option>
                    ))}
                  </select>
                </div>

                <span className="dependency-add-arrow">→</span>

                <div className="dependency-select-group">
                  <label>{isNorwegian ? 'Til sjekkpunkt' : 'To checkpoint'}</label>
                  <select
                    value={toId}
                    onChange={e => setToId(e.target.value)}
                    className="dependency-select"
                  >
                    <option value="">{isNorwegian ? '— velg —' : '— select —'}</option>
                    {allCheckpoints
                      .filter(item => item.cpId !== fromId)
                      .map(item => (
                        <option key={item.cpId} value={item.cpId}>
                          [{item.flowName}] {item.cpName}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <button
                className="dependency-add-btn"
                onClick={handleAdd}
                disabled={!fromId || !toId || fromId === toId}
              >
                {isNorwegian ? 'Legg til' : 'Add'}
              </button>
            </div>
          </div>
        )}

        {allCheckpoints.length < 2 && (
          <p className="dependency-no-checkpoints">
            {isNorwegian
              ? 'Du trenger minst to sjekkpunkter for å opprette avhengigheter.'
              : 'You need at least two checkpoints to create dependencies.'}
          </p>
        )}
      </div>
    </div>
  )
}
